<script setup lang="ts">
import CPopover from '@lib/components/CPopover/CPopover.vue'
import CTag from '@lib/components/CTag/CTag.vue'
import { moreCountBuffer } from '@lib/constants/more.const'
import useThemeVars from '@lib/composables/useThemeVars'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue: { label: string; key?: string | number }[]
    maxItemsToShow?: number
    rowIndex: number
    empty?: string
    isClickable?: boolean
  }>(),
  {
    modelValue: () => [],
    maxItemsToShow: 1,
    empty: '-',
    isClickable: true,
  },
)

const emit = defineEmits<{
  (e: 'click', key?: string | number): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const itemsToShow = computed(() => {
  if (props.modelValue.length > props.maxItemsToShow + moreCountBuffer) {
    return props.modelValue.slice(0, props.maxItemsToShow)
  }
  return props.modelValue
})
const remainingItems = computed(() => props.modelValue.slice(itemsToShow.value.length))
</script>

<template>
  <div class="w-full flex items-center justify-between c-table-more">
    <div v-if="itemsToShow.length" class="h-full flex items-center min-w-0">
      <div
        class="mr-1 text-truncate text-ellipsis"
        :class="{ 'c-table-more__clickable': isClickable, underline: isClickable }"
      >
        <span
          v-for="(item, index) in itemsToShow"
          :key="index"
          class="c-table-more__items"
          size="small"
          @click="emit('click', item.key)"
        >
          {{ item.label }}
        </span>
        <CPopover v-if="remainingItems.length" :placement="rowIndex === 0 ? 'bottom' : 'top'" trigger="click">
          <template #trigger>
            <span class="underline underline-solid cursor-pointer select-none ml-1 c-table-more__more-items-trigger">
              {{
                t('canvasUI.components.tables.andCountMore', {
                  count: remainingItems.length,
                })
              }}
            </span>
          </template>
          <div>
            <CTag
              v-for="(item, index) in remainingItems"
              :key="index"
              class="mr-1 c-table-more__more-items"
              :class="{ 'c-table-more__clickable': isClickable, underline: isClickable }"
              :round="false"
              size="small"
              @click="emit('click', item.key)"
            >
              {{ item.label }}
            </CTag>
          </div>
        </CPopover>
      </div>
    </div>
    <div v-else class="c-table-more__empty">
      {{ empty }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.c-table-more {
  &__empty {
    color: v-bind('themeVars.text3');
  }
  &__items,
  &__more-items {
    font-size: 14px;
    line-height: normal;
    letter-spacing: 0.4px;
  }
  &__more-items-trigger {
    color: v-bind('themeVars.text');
  }

  &__clickable {
    cursor: pointer;
    color: v-bind('themeVars.primary');
  }
}
</style>
